// Report scheduling API wrappers
import { api } from "./apiClient";

// Schedule frequency options, ordered for display in the modal. These must stay in
// sync with the backend ScheduleFrequency enum — it rejects any other value with a 422.
export const FREQUENCY_OPTIONS = [
  { value: "weekly", label: "Every week" },
  { value: "biweekly", label: "Every other week" },
  { value: "monthly", label: "Every month" },
];

// The schedulable report types, in display order. `target` describes the extra
// input the type needs (a URL for audits, a query for LLM visibility); types with
// `target: null` run against the account's saved business profile. `usageFeature`
// is the /user/usage feature key the type consumes (null = unmetered, never blocked).
export const SCHEDULE_REPORT_TYPES = [
  {
    value: "audit",
    label: "AI Audit",
    usageFeature: "audits",
    target: { required: true, label: "Website URL", placeholder: "https://example.com" },
  },
  {
    value: "llm_visibility",
    label: "LLM Visibility",
    usageFeature: "prompt_tests",
    target: { required: true, label: "Query", placeholder: "best hotels in Lisbon" },
  },
  { value: "persona", label: "Persona Visibility", usageFeature: "persona_reports", target: null },
  {
    value: "ota_vs_direct",
    label: "OTA vs Direct",
    usageFeature: "ota_queries",
    target: { required: false, label: "Query (optional)", placeholder: "hotels in Lisbon" },
  },
  { value: "perception", label: "AI Perception", usageFeature: null, target: null },
];

// Lookup from a report type value to its metadata.
export const SCHEDULE_REPORT_TYPE_BY_VALUE = Object.fromEntries(
  SCHEDULE_REPORT_TYPES.map((t) => [t.value, t]),
);

// Lookup from frequency value to its display label.
export const FREQUENCY_LABELS = Object.fromEntries(
  FREQUENCY_OPTIONS.map((f) => [f.value, f.label]),
);

/**
 * Create a report schedule.
 *
 * @param {object} payload - Fields: report_type, target, frequency, second_email?
 * @returns {Promise} Axios response with the created ReportScheduleResponse
 */
export const createSchedule = (payload) => {
  return api.post("/user/report-schedules", payload);
};

/**
 * List the current user's active schedules, optionally filtered by report type.
 *
 * @param {string} [reportType] - Report type to filter by ("audit", "llm_visibility", "persona",
 *   "ota_vs_direct", "perception")
 * @param {object} [config] - Additional axios config (e.g. an AbortController signal)
 * @returns {Promise} Axios response with a list of ReportScheduleResponse
 */
export const listSchedules = (reportType, config) => {
  const query = reportType ? `?report_type=${encodeURIComponent(reportType)}` : "";
  return api.get(`/user/report-schedules${query}`, false, config);
};

/**
 * Cancel (delete) a schedule by id.
 *
 * @param {string} id - Schedule id
 * @returns {Promise} Axios response
 */
export const deleteSchedule = (id) => {
  return api.delete(`/user/report-schedules/${id}`);
};
